import type { Annotation, Rect } from './geometry';

/** One committed editor state. In-progress strokes are not part of it. */
export interface EditorSnapshot {
  selection: Rect | null;
  annotations: Annotation[];
}

export interface EditHistory {
  past: EditorSnapshot[];
  future: EditorSnapshot[];
}

export function emptyHistory(): EditHistory {
  return { past: [], future: [] };
}

export function cloneSnapshot(snapshot: EditorSnapshot): EditorSnapshot {
  return {
    selection: snapshot.selection ? { ...snapshot.selection } : null,
    annotations: snapshot.annotations.map(cloneAnnotation),
  };
}

export function sameSnapshot(a: EditorSnapshot, b: EditorSnapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Push `before` and drop anything that had been redoable.
 * Returns the same history when the edit did not change the snapshot.
 */
export function recordEdit(
  history: EditHistory,
  before: EditorSnapshot,
  after: EditorSnapshot,
): EditHistory {
  if (sameSnapshot(before, after)) {
    return history;
  }
  return {
    past: [...history.past, cloneSnapshot(before)],
    future: [],
  };
}

export function undoEdit(
  history: EditHistory,
  present: EditorSnapshot,
): { history: EditHistory; present: EditorSnapshot } | null {
  const previous = history.past[history.past.length - 1];
  if (!previous) {
    return null;
  }
  return {
    history: {
      past: history.past.slice(0, -1),
      future: [...history.future, cloneSnapshot(present)],
    },
    present: cloneSnapshot(previous),
  };
}

export function redoEdit(
  history: EditHistory,
  present: EditorSnapshot,
): { history: EditHistory; present: EditorSnapshot } | null {
  const next = history.future[history.future.length - 1];
  if (!next) {
    return null;
  }
  return {
    history: {
      past: [...history.past, cloneSnapshot(present)],
      future: history.future.slice(0, -1),
    },
    present: cloneSnapshot(next),
  };
}

function cloneAnnotation(item: Annotation): Annotation {
  if (item.kind === 'brush') {
    return {
      ...item,
      points: item.points.map((point) => ({ ...point })),
    };
  }
  if (item.kind === 'mosaic') {
    return {
      ...item,
      tiles: item.tiles.map((tile) => ({ ...tile })),
    };
  }
  return { ...item };
}
